import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';

export const useNetworkManager = () => {
  const [isAddingNetwork, setIsAddingNetwork] = useState(false);
  const [currentChainId, setCurrentChainId] = useState(null);
  const [networkParams, setNetworkParams] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState(false);

  // 检查MetaMask是否安装
  const checkMetaMask = useCallback(() => {
    // 明确检查MetaMask，而不是其他钱包
    const hasMetaMask = typeof window !== 'undefined' && 
      (window.ethereum?.isMetaMask || 
       (window.ethereum && !window.ethereum.isOKX && !window.ethereum.isTokenPocket));
    setIsMetaMaskInstalled(hasMetaMask);
    return hasMetaMask;
  }, []);

  // 获取网络参数
  const fetchNetworkParams = useCallback(async () => {
    try {
      const response = await fetch('/network_params.json');
      const params = await response.json();
      setNetworkParams(params);
      return params;
    } catch (error) {
      console.error('Error fetching network params:', error);
      message.error('Failed to load network parameters');
      return null;
    }
  }, []);

  // 获取MetaMask实例
  const getMetaMaskProvider = useCallback(() => {
    if (window.ethereum?.isMetaMask) {
      return window.ethereum;
    }
    // 如果window.ethereum不是MetaMask，尝试从其他钱包中获取MetaMask
    if (window.ethereum && !window.ethereum.isMetaMask) {
      // 检查是否有其他钱包实例
      const providers = window.ethereum.providers || [];
      const metamaskProvider = providers.find(provider => provider.isMetaMask);
      return metamaskProvider || null;
    }
    return window.ethereum;
  }, []);

  // 检查网络状态
  const checkNetworkStatus = useCallback(async () => {
    if (!checkMetaMask()) return;

    const provider = getMetaMaskProvider();
    if (!provider) {
      console.error('MetaMask provider not found');
      return;
    }

    try {
      const chainId = await provider.request({ method: 'eth_chainId' });
      setCurrentChainId(chainId);
      
      // 获取网络参数
      await fetchNetworkParams();

      // 检查是否已连接
      const accounts = await provider.request({ method: 'eth_accounts' });
      setIsConnected(accounts.length > 0);
    } catch (error) {
      console.error('Error checking network status:', error);
    }
  }, [checkMetaMask, fetchNetworkParams, getMetaMaskProvider]);

  // 添加网络
  const addNetwork = useCallback(async () => {
    if (!checkMetaMask()) {
      message.error('MetaMask not detected. Please install MetaMask first.');
      return false;
    }

    if (!networkParams) {
      message.error('Network parameters not available.');
      return false;
    }

    const provider = getMetaMaskProvider();
    if (!provider) {
      message.error('MetaMask provider not found. Please ensure MetaMask is installed and unlocked.');
      return false;
    }

    setIsAddingNetwork(true);
    
    try {
      // 检查是否已经添加了该网络
      if (networkParams.chainId === currentChainId) {
        message.info(`${networkParams.chainName} is already connected.`);
        return true;
      }

      // 先尝试切换到网络
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: networkParams.chainId }],
        });
        message.success(`Successfully switched to ${networkParams.chainName}`);
        return true;
      } catch (switchError) {
        // 如果网络不存在（4902错误），则添加网络
        if (switchError.code === 4902) {
          console.log('Network not found, adding it first...');
          
          // 清理网络参数，确保格式正确
          const cleanNetworkParams = {
            chainId: networkParams.chainId,
            chainName: networkParams.chainName,
            nativeCurrency: networkParams.nativeCurrency,
            rpcUrls: networkParams.rpcUrls.map(url => url.trim()), // 移除空格
            blockExplorerUrls: networkParams.blockExplorerUrls.map(url => url.trim()), // 移除空格
            ...(networkParams.iconUrls && { iconUrls: networkParams.iconUrls.map(url => url.trim()) })
          };

          // 添加网络
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [cleanNetworkParams],
          });
          
          message.success(`${networkParams.chainName} has been added to MetaMask successfully!`);
          
          // 添加成功后，再次尝试切换
          try {
            await provider.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: networkParams.chainId }],
            });
            message.success(`Successfully switched to ${networkParams.chainName}`);
          } catch (finalSwitchError) {
            console.error('Failed to switch after adding network:', finalSwitchError);
            message.warning('Network added but failed to switch. Please switch manually.');
          }
          
          return true;
        } else {
          throw switchError;
        }
      }
    } catch (error) {
      console.error('Error adding network:', error);
      if (error.code === 4001) {
        message.warning('User rejected the request to add network.');
      } else {
        message.error(`Failed to add network: ${error.message}`);
      }
      return false;
    } finally {
      setIsAddingNetwork(false);
    }
  }, [checkMetaMask, networkParams, currentChainId, getMetaMaskProvider]);

  // 连接钱包
  const connectWallet = useCallback(async () => {
    if (!checkMetaMask()) {
      message.error('MetaMask not detected. Please install MetaMask first.');
      return false;
    }

    const provider = getMetaMaskProvider();
    if (!provider) {
      message.error('MetaMask provider not found. Please ensure MetaMask is installed and unlocked.');
      return false;
    }

    try {
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      setIsConnected(accounts.length > 0);
      if (accounts.length > 0) {
        message.success('Wallet connected successfully!');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error connecting wallet:', error);
      if (error.code === 4001) {
        message.warning('User rejected the connection request.');
      } else {
        message.error(`Failed to connect wallet: ${error.message}`);
      }
      return false;
    }
  }, [checkMetaMask, getMetaMaskProvider]);

  // 监听网络变化
  useEffect(() => {
    checkNetworkStatus();

    const provider = getMetaMaskProvider();
    if (provider) {
      const handleChainChanged = (chainId) => {
        setCurrentChainId(chainId);
        checkNetworkStatus();
      };

      const handleAccountsChanged = (accounts) => {
        setIsConnected(accounts.length > 0);
      };

      provider.on('chainChanged', handleChainChanged);
      provider.on('accountsChanged', handleAccountsChanged);

      return () => {
        provider.removeListener('chainChanged', handleChainChanged);
        provider.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, [checkNetworkStatus, getMetaMaskProvider]);

  // 计算当前状态
  const getNetworkStatus = useCallback(() => {
    if (!isMetaMaskInstalled) {
      return {
        status: 'no-metamask',
        text: 'Install MetaMask',
        canConnect: false,
        canAddNetwork: false
      };
    }

    if (!isConnected) {
      return {
        status: 'not-connected',
        text: 'Connect Wallet',
        canConnect: true,
        canAddNetwork: false
      };
    }

    if (networkParams?.chainId === currentChainId) {
      return {
        status: 'connected',
        text: 'Connected',
        canConnect: false,
        canAddNetwork: false
      };
    }

    return {
      status: 'wrong-network',
      text: 'Add Mova Network',
      canConnect: false,
      canAddNetwork: true
    };
  }, [isMetaMaskInstalled, isConnected, networkParams, currentChainId]);

  return {
    // 状态
    isAddingNetwork,
    currentChainId,
    networkParams,
    isConnected,
    isMetaMaskInstalled,
    
    // 计算状态
    networkStatus: getNetworkStatus(),
    
    // 方法
    addNetwork,
    connectWallet,
    checkNetworkStatus,
    checkMetaMask
  };
}; 